import React, { useState } from 'react';
import { useTranslation } from '../context/LanguageContext';
import { apiService } from '../services/api';
import { CreateClientRequest } from '../types/api';
import './Modal.css';

interface AddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClientAdded: () => void;
}

const AddClientModal: React.FC<AddClientModalProps> = ({ isOpen, onClose, onClientAdded }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CreateClientRequest>({
    name: '',
    email: '',
    phone: '',
    password: '',
    isActive: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await apiService.createClient(formData);
      
      if (response.success) {
        onClientAdded();
        onClose();
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          password: '',
          isActive: true
        });
      } else {
        setError(response.message || t('failedToCreateClient'));
      }
    } catch (err: any) {
      console.error('Error creating client:', err);
      setError(
        err.response?.data?.message || 
        err.message || 
        t('errorCreatingClient')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = formData.name && formData.email && formData.phone && formData.password.length >= 6;

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('addNewClient')}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="name">{t('fullName')} *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={isLoading}
              placeholder={t('enterClientsFullName')}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">{t('email')} *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              disabled={isLoading}
              placeholder={t('enterClientsEmail')}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="phone">{t('phone')} *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              disabled={isLoading}
              placeholder={t('enterClientsPhone')}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">{t('password')} *</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              minLength={6}
              disabled={isLoading}
              placeholder={t('enterInitialPassword')}
            />
          </div>
          
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              <span className="checkbox-text">{t('activeAccount')}</span>
            </label>
          </div>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={!isFormValid || isLoading}
              className="btn-primary"
            >
              {isLoading ? t('creating') : t('createClient')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddClientModal; 